'use client';

import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { usePanelState, type PanelType } from '@/shared/hooks';
import { cn } from '@/shared/lib/cn';
import { ClipboardText, Sparkle, CompassTool, Calculator } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useProject } from '../hooks/use-projects';
import { useConcepts } from '@/features/design/concept/hooks/use-concepts';
import { albumApi } from '@/features/design/album';
import { useProjectEstimates } from '@/features/design/estimate';
import { useSidebarStore } from '@/shared/stores/sidebar-store';
import { cacheKeys } from '@/shared/lib/cache-invalidation';

type IconWeight = 'regular' | 'fill';
type IconComponent = React.ComponentType<{ className?: string; weight?: IconWeight }>;

interface PanelMenuItem {
  type: 'panel';
  panel: PanelType;
  label: string;
  icon: IconComponent;
  showIndicator?: boolean;
  count?: number;
}

interface LinkMenuItem {
  type: 'link';
  href: string;
  label: string;
  icon: IconComponent;
  showIndicator?: boolean;
  count?: number;
}

type MenuItem = PanelMenuItem | LinkMenuItem;

interface ProjectSettingsMenuProps {
  projectId: string;
}

/**
 * Project settings menu for the sidebar.
 * Brief (which now also hosts the building's photos AND references) sits in its
 * own block as the input step; the output trio — Concepts, Estimates, Albums —
 * follows after a group gap, mirroring the Apple/Android sidebars.
 */
export function ProjectSettingsMenu({ projectId }: ProjectSettingsMenuProps) {
  const t = useTranslations('project');
  const tAlbum = useTranslations('album');
  const tEstimate = useTranslations('common.estimate');
  const pathname = usePathname();
  const { panel, setPanel } = usePanelState();
  const collapsed = useSidebarStore((s) => s.collapsed);
  const { data: project } = useProject(projectId);
  const { data: concepts = [] } = useConcepts(projectId);
  const { data: projectAlbums = [] } = useQuery({
    queryKey: cacheKeys.projectAlbums.list(projectId),
    queryFn: () => albumApi.listByProject(projectId),
    enabled: !!projectId,
  });

  // Check if project goals are empty
  const projectGoals = project?.data?.attributes?.goals;
  const hasGoals = !!projectGoals && projectGoals.trim() !== '';

  const conceptsCount = concepts.length;
  const albumsCount = projectAlbums.reduce(
    (sum, entry) =>
      sum + entry.albums.filter((a) => a.status === 'completed').length,
    0
  );
  // All statuses — matches what the project Estimates page lists.
  const { data: projectEstimates = [] } = useProjectEstimates(projectId);
  const estimatesCount = projectEstimates.length;

  // Brief (input step) reads as its own block; the output trio
  // (Designs/Estimates/Blueprints) follows after a group gap. Mirrors the
  // Brief/output separation on Apple (SidebarView) and Android (FacadeSidebar).
  const briefItems: MenuItem[] = [
    {
      type: 'link',
      href: `/project/${projectId}/goals`,
      label: t('projectGoalsNav'),
      icon: ClipboardText,
      showIndicator: !hasGoals,
    },
  ];

  // The standalone References nav entry was removed (Phase 2): references now
  // live inside the Brief page's unified materials grid.
  // Estimates before Albums/Blueprints (cross-platform parity).
  const outputItems: MenuItem[] = [
    {
      type: 'link',
      href: `/project/${projectId}/concepts`,
      label: t('conceptHistory'),
      icon: Sparkle,
      count: conceptsCount > 0 ? conceptsCount : undefined,
    },
    {
      type: 'link',
      href: `/project/${projectId}/estimates`,
      label: tEstimate('page.title'),
      icon: Calculator,
      count: estimatesCount > 0 ? estimatesCount : undefined,
    },
    {
      type: 'link',
      href: `/project/${projectId}/albums`,
      label: tAlbum('albums'),
      icon: CompassTool,
      count: albumsCount > 0 ? albumsCount : undefined,
    },
  ];

  const renderItem = (item: MenuItem) => {
    const Icon = item.icon;

    if (item.type === 'link') {
      const isActive = pathname === item.href;
      return (
        <Link
          key={item.href}
          href={item.href}
          className={cn(
            'w-full flex items-center rounded-lg text-list-item-subtitle transition-colors',
            collapsed ? 'justify-center px-0 py-2' : 'gap-3 px-3 py-2',
            isActive
              ? 'bg-foreground/10 text-primary font-medium'
              : 'text-foreground hover:bg-foreground/5'
          )}
          title={collapsed ? item.label : undefined}
        >
          <Icon
            className={cn(
              'h-5 w-5 flex-shrink-0',
              isActive ? 'text-primary' : 'text-foreground'
            )}
            weight={isActive ? 'fill' : 'regular'}
          />
          {!collapsed && <span className="truncate">{item.label}</span>}
          {!collapsed && item.showIndicator && (
            <span className="ms-auto min-w-6 flex items-center justify-center flex-shrink-0">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
            </span>
          )}
          {!collapsed && item.count !== undefined && (
            <span className="ms-auto min-w-6 flex items-center justify-center flex-shrink-0">
              <span className="text-muted-foreground text-chip">
                {item.count}
              </span>
            </span>
          )}
        </Link>
      );
    }

    // Panel button
    const isActive = panel === item.panel;
    return (
      <button
        key={item.panel}
        onClick={() => setPanel(isActive ? null : item.panel)}
        className={cn(
          'w-full flex items-center rounded-lg text-list-item-subtitle transition-colors text-start',
          collapsed ? 'justify-center px-0 py-2' : 'gap-3 px-3 py-2',
          isActive
            ? 'bg-foreground/10 text-primary font-medium'
            : 'text-foreground hover:bg-foreground/5'
        )}
        title={collapsed ? item.label : undefined}
      >
        <Icon
          className={cn(
            'h-5 w-5 flex-shrink-0',
            isActive ? 'text-primary' : 'text-foreground'
          )}
          weight={isActive ? 'fill' : 'regular'}
        />
        {!collapsed && <span className="truncate">{item.label}</span>}
        {!collapsed && item.showIndicator && (
          <span className="ms-auto min-w-6 flex items-center justify-center flex-shrink-0">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
          </span>
        )}
        {!collapsed && item.count !== undefined && (
          <span className="ms-auto min-w-6 flex items-center justify-center flex-shrink-0">
            <span className="bg-muted text-muted-foreground px-1.5 py-0.5 text-chip rounded-full">
              {item.count}
            </span>
          </span>
        )}
      </button>
    );
  };

  return (
    // mt-6 = group gap below the standalone building entry, matching the gap
    // before the History/Account/Help block in AppSidebar.
    <nav className="mt-6 py-1">
      <div className="space-y-1.5">{briefItems.map(renderItem)}</div>
      {/* mt-6 = same group gap, splitting Brief (input) from the output trio. */}
      <div className="mt-6 space-y-1.5">{outputItems.map(renderItem)}</div>
    </nav>
  );
}
